import React from "react";
import { Button, Card, Switch, Label, Separator, useAppSettings } from "src/common";
import { Environment, LlmModel, VlmModel, VqaThumbnailAppearance } from "src/common/types/settings";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "src/common";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "src/common";
import {
    ArrowLeft,
    Bell,
    Palette,
    Brain,
    MessageSquareText,
} from "lucide-react";

interface SettingsAppProps {
    onBack: () => void;
    onClose: () => void;
}

export function SettingsApp({ onBack, onClose }: SettingsAppProps) {
    const { theme, setTheme, developerMode: developerTool, setDeveloperMode: setDeveloperTool, environment, setEnvironment, vqaThumbnailAppearance, setVqaThumbnailAppearance, vlmModel, setVlmModel, llmModel, setLlmModel } = useAppSettings();


    return (
        <div className="fixed inset-0 bg-background text-foreground z-50 flex flex-col">
            {/* Header */}
            <div className="flex items-center gap-4 p-4 border-b border-border">
                <Button
                    variant="ghost"
                    size="sm"
                    className="p-2"
                    onClick={onBack}
                >
                    <ArrowLeft className="w-5 h-5" />
                </Button>
                <h1 className="!text-xl">Settings</h1>
            </div>

            {/* Tabs - Make scrollable */}
            <Tabs defaultValue="general" className="w-full flex-1 flex flex-col overflow-hidden">
                <div className="border-b border-border px-4 pt-2">
                    <TabsList className="bg-transparent w-full justify-start">
                        <TabsTrigger
                            value="general"
                            className="data-[state=active]:bg-muted"
                        >
                            General
                        </TabsTrigger>
                        <TabsTrigger
                            value="apps"
                            className="data-[state=active]:bg-muted"
                        >
                            Apps
                        </TabsTrigger>
                    </TabsList>
                </div>

                {/* General Settings Tab */}
                <TabsContent
                    value="general"
                    className="p-4 space-y-6 pb-24 overflow-y-auto flex-1"
                >
                    {/* Version Info */}
                    <Card className="bg-card border-border p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                                <Palette className="w-5 h-5 text-blue-400 light:text-blue-600" />
                            </div>
                            <h2 className="!text-lg">Version</h2>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <Label className="text-sm">
                                    Frontend Version:
                                </Label>
                                <span className="text-sm text-muted-foreground">
                                    879358735
                                </span>
                            </div>

                            <div className="flex items-center justify-between">
                                <Label className="text-sm">
                                    Backend Version:
                                </Label>
                                <span className="text-sm text-muted-foreground">
                                    145746867
                                </span>
                            </div>
                        </div>
                    </Card>

                    {/* Notifications */}
                    <Card className="bg-card border-border p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                                <Bell className="w-5 h-5 text-blue-400 light:text-blue-600" />
                            </div>
                            <h2 className="!text-lg">
                                Notifications
                            </h2>
                        </div>
                    </Card>

                    {/* Display Settings */}
                    <Card className="bg-card border-border p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                                <Palette className="w-5 h-5 text-blue-400 light:text-blue-600" />
                            </div>
                            <h2 className="!text-lg">Display</h2>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>
                                        Dark Mode
                                    </Label>
                                    <p className="text-sm text-muted-foreground">
                                        Use dark theme
                                    </p>
                                </div>
                                <Switch
                                    checked={theme === 'dark'}
                                    onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                                    className="data-[state=checked]:bg-blue-500"
                                />
                            </div>
                        </div>
                    </Card>

                    {/* Developer Settings */}
                    <Card className="bg-card border-border p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                                <Brain className="w-5 h-5 text-purple-400" />
                            </div>
                            <h2 className="!text-lg">Developer</h2>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>
                                        Developer Tool
                                    </Label>
                                    <p className="text-sm text-muted-foreground">
                                        Enable debugging features
                                    </p>
                                </div>
                                <Switch
                                    checked={developerTool}
                                    onCheckedChange={setDeveloperTool}
                                    className="data-[state=checked]:bg-blue-500"
                                />
                            </div>

                            <Separator className="bg-border" />

                            <div className="flex items-center justify-between">
                                <div>
                                    <Label>
                                        Prototype Mode
                                    </Label>
                                    <p className="text-sm text-muted-foreground">
                                        Enable prototype features
                                    </p>
                                </div>
                                <Switch
                                    checked={environment === Environment.PROTOTYPE}
                                    onCheckedChange={(checked) => setEnvironment(checked ? Environment.PROTOTYPE : Environment.PRODUCTION)}
                                    className="data-[state=checked]:bg-blue-500"
                                />
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                {/* Apps Settings Tab */}
                <TabsContent
                    value="apps"
                    className="p-4 space-y-6 pb-24 overflow-y-auto flex-1"
                >
                    {/* Rogers VQA Settings */}
                    <Card className="bg-card border-border p-4">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                                <MessageSquareText className="w-5 h-5 text-white" />
                            </div>
                            <h2 className="!text-lg">Rogers VQA</h2>
                        </div>

                        <div>
                            <Label
                                htmlFor="thumbnail-appearance"
                                className="text-muted-foreground mb-2 block"
                            >
                                Thumbnail Appearance
                            </Label>
                            <Select
                                value={vqaThumbnailAppearance}
                                onValueChange={setVqaThumbnailAppearance}
                            >
                                <SelectTrigger className="bg-muted border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                    <SelectItem
                                        value={VqaThumbnailAppearance.FIRST_FRAME}
                                    >
                                        First Frame
                                    </SelectItem>
                                    <SelectItem
                                        value={VqaThumbnailAppearance.MIDDLE_FRAME}
                                    >
                                        Middle Frame
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label
                                htmlFor="vlm-model"
                                className="text-muted-foreground mb-2 block"
                            >
                                VLM Model
                            </Label>
                            <Select
                                value={vlmModel}
                                onValueChange={(v) => setVlmModel(v as VlmModel)}
                            >
                                <SelectTrigger className="bg-muted border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                    <SelectItem value={VlmModel.QWEN_30B}>Qwen 30B</SelectItem>
                                    <SelectItem value={VlmModel.GEMINI_3_1_PRO_PREVIEW}>Gemini 3.1 Pro Preview</SelectItem>
                                    <SelectItem value={VlmModel.GPT_5_4}>GPT 5.4</SelectItem>
                                    <SelectItem value={VlmModel.SVD_SHORT}>SVD Short</SelectItem>
                                    <SelectItem value={VlmModel.CUSTOM_SVD_LONG}>SVD Long</SelectItem>
                                    <SelectItem value={VlmModel.FLYWHEEL_30B}>Flywheel 30B</SelectItem>
                                    <SelectItem value={VlmModel.CLAUDE_OPUS}>Claude Opus 4.6</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div>
                            <Label
                                htmlFor="llm-model"
                                className="text-muted-foreground mb-2 block"
                            >
                                LLM Model
                            </Label>
                            <Select
                                value={llmModel}
                                onValueChange={(v) => setLlmModel(v as LlmModel)}
                            >
                                <SelectTrigger className="bg-muted border-border">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border">
                                    <SelectItem value={LlmModel.US_CLAUDE_OPUS_4_6}>Claude Opus 4.6</SelectItem>
                                    <SelectItem value={LlmModel.US_SONNET_4_6}>Claude Sonnet 4.6</SelectItem>
                                    <SelectItem value={LlmModel.US_HAIKU_4_5}>Claude Haiku 4.5</SelectItem>
                                    {/* <SelectItem value={LlmModel.GEMINI_3_1_PRO_PREVIEW} disabled>Gemini 3.1 Pro Preview</SelectItem> */}
                                    <SelectItem value={LlmModel.GEMINI_3_1_FLASH_LITE_PREVIEW}>Gemini 3.1 Flash Lite Preview</SelectItem>
                                    {/* <SelectItem value={LlmModel.GEMINI_3_FLASH_PREVIEW} disabled>Gemini 3 Flash Preview</SelectItem> */}
                                    {/* <SelectItem value={LlmModel.GEMINI_2_5_PRO} disabled>Gemini 2.5 Pro</SelectItem> */}
                                    <SelectItem value={LlmModel.GPT_5_4}>GPT 5.4</SelectItem>
                                    {/* <SelectItem value={LlmModel.GROK_4_20} disabled>Grok 4.20</SelectItem> */}
                                </SelectContent>
                            </Select>
                        </div>

                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}